import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Crown, LogOut, ChevronRight, UserCircle2,
  Camera, Dumbbell, Heart, Flame, ShoppingBag, Shirt,
  Check, Trophy, ArrowUpRight, Droplet, Scissors, Wind, TrendingUp, Lock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import MilestoneCelebration from "@/components/MilestoneCelebration";
import RemindersBanner from "@/components/RemindersBanner";
import { NovaLogo } from "@/components/NovaLogo";
import { getPref, PREF_CELEBRATIONS, getNewlyUnlockedMilestones } from "@/lib/preferences";

// ─────────────────────────────────────────────────────────────────────────────
// Home — Glow Journey edition
// ─────────────────────────────────────────────────────────────────────────────
// Apple Fitness × Duolingo vibe. No big number score on the home screen.
// Celebrates consistency + milestones instead of judging users with /100.
// Sections (top → bottom):
//   1. Personal greeting (time-aware, name)
//   2. Glow Journey — current stage, milestone progress, next milestone
//   3. Streak ring + Today's activity row
//   4. Today's Focus — single, personalized next move
//   5. Quick Actions — Scan Food · Generate Outfit · Start Workout · Talk to Coach
//   6. Recent Progress — counts across pillars
//   7. Daily Coach Tip — serif italic, calm
// ─────────────────────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { key: "scan-food",    label: "Scan Food",       sub: "Log a meal",      icon: Camera,   route: "/nutrition/scan",     testId: "home-quick-scan"     },
  { key: "workout",      label: "Start Workout",   sub: "Move today",      icon: Dumbbell, route: "/improve/fitness",    testId: "home-quick-workout"  },
  { key: "coach",        label: "Talk to Coach",   sub: "Get unstuck",     icon: Heart,    route: "/coach",              testId: "home-quick-coach"    },
  { key: "progress",     label: "My Progress",     sub: "See milestones",  icon: Sparkles, route: "/progress",           testId: "home-quick-progress" },
];

// Journey stages — based on milestones unlocked (backend returns 9 total).
const STAGES = [
  { min: 0,  name: "Just Beginning",        sub: "Take your first step today." },
  { min: 1,  name: "Starting Out",          sub: "Tiny wins are still wins."   },
  { min: 3,  name: "Building Healthy Habits", sub: "You're showing up. That's the whole game." },
  { min: 5,  name: "Finding Your Rhythm",   sub: "Patterns are forming. Stay with it." },
  { min: 7,  name: "Glowing Up",            sub: "This is what consistency looks like." },
  { min: 9,  name: "Glow Master",           sub: "You've built something real." },
];

function stageFor(unlockedCount) {
  let chosen = STAGES[0];
  for (const s of STAGES) if (unlockedCount >= s.min) chosen = s;
  return chosen;
}

function greetingFor(hour) {
  if (hour < 5)  return "Late night";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  if (hour < 21) return "Good evening";
  return "Good night";
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function GlowJourneyCard({ milestones, onOpenProgress }) {
  const unlocked = milestones.filter((m) => m.unlocked);
  const total = milestones.length || 9;
  const unlockedCount = unlocked.length;
  const pct = total > 0 ? Math.round((unlockedCount / total) * 100) : 0;
  const stage = stageFor(unlockedCount);
  const nextMilestone = milestones.find((m) => !m.unlocked) || null;
  const lastUnlocked = unlocked[unlocked.length - 1] || null;

  return (
    <button
      type="button"
      onClick={onOpenProgress}
      data-testid="home-glow-journey"
      className="glow-journey-card"
    >
      <div className="glow-journey-header">
        <div className="glow-journey-eyebrow">
          <Sparkles size={11} strokeWidth={2} />
          <span>GLOW JOURNEY</span>
        </div>
        <ArrowUpRight size={16} className="glow-journey-arrow" />
      </div>

      <div className="glow-journey-stage" data-testid="home-journey-stage">{stage.name}</div>
      <div className="glow-journey-sub">{stage.sub}</div>

      {/* Milestone dots */}
      <div className="glow-journey-dots" data-testid="home-journey-dots">
        {milestones.map((m, i) => (
          <div
            key={m.id || i}
            className={"glow-dot " + (m.unlocked ? "glow-dot-on" : "glow-dot-off")}
            title={m.name}
          >
            {m.unlocked && <Check size={9} strokeWidth={3} />}
          </div>
        ))}
      </div>

      <div className="glow-journey-bar">
        <div className="glow-journey-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="glow-journey-footer">
        <div className="glow-journey-count">
          <Trophy size={12} strokeWidth={2} />
          <span data-testid="home-journey-count">{unlockedCount}/{total} milestones</span>
        </div>
        {nextMilestone ? (
          <div className="glow-journey-next" data-testid="home-journey-next">
            Next · <em>{nextMilestone.name}</em>
          </div>
        ) : (
          <div className="glow-journey-next" data-testid="home-journey-next">
            <em>All milestones reached</em>
          </div>
        )}
      </div>

      {lastUnlocked && (
        <div className="glow-journey-recent" data-testid="home-journey-recent">
          <span className="glow-journey-recent-badge">★</span>
          Latest unlock: <strong>{lastUnlocked.name}</strong>
        </div>
      )}
    </button>
  );
}

function StreakRing({ current, active, longest }) {
  return (
    <div className="streak-ring-card" data-testid="home-streak">
      <div className="streak-ring-icon">
        <Flame size={20} strokeWidth={1.8} />
      </div>
      <div className="streak-ring-body">
        <div className="streak-ring-eyebrow">{active ? "STREAK" : "BUILD YOUR STREAK"}</div>
        <div className="streak-ring-num">
          <span className="streak-ring-big">{current || 0}</span>
          <span className="streak-ring-unit">day{current === 1 ? "" : "s"}</span>
        </div>
        <div className="streak-ring-foot">
          {active
            ? (longest && longest > current ? `Personal best ${longest}` : "Keep it going today")
            : (current ? "Show up today to keep it" : "Start today")}
        </div>
      </div>
    </div>
  );
}

function TodayActivityCard({ caloriesToday, scansToday, briefMessage }) {
  return (
    <div className="today-activity-card" data-testid="home-today-activity">
      <div className="streak-ring-eyebrow">TODAY</div>
      <div className="today-activity-grid">
        <div>
          <div className="today-activity-num">{caloriesToday != null ? caloriesToday : "—"}</div>
          <div className="today-activity-label">kcal logged</div>
        </div>
        <div>
          <div className="today-activity-num">{scansToday ?? 0}</div>
          <div className="today-activity-label">scan{scansToday === 1 ? "" : "s"}</div>
        </div>
      </div>
      {briefMessage && (
        <div className="today-activity-foot">{briefMessage.length > 80 ? briefMessage.slice(0, 78) + "…" : briefMessage}</div>
      )}
    </div>
  );
}

function TodayFocusCard({ opportunity, onAct }) {
  if (!opportunity) return null;
  return (
    <button
      type="button"
      onClick={onAct}
      className="today-focus-card"
      data-testid="home-today-focus"
    >
      <div className="today-focus-eyebrow">
        <span className="today-focus-dot" />
        TODAY&apos;S FOCUS
      </div>
      <div className="today-focus-title">
        Your dream body <em className="serif">starts here</em>.
      </div>
      <div className="today-focus-sub">
        Train with purpose. Stay consistent. Become the version of yourself you&apos;ve been working toward.
      </div>
      <div className="today-focus-cta">
        <span>Start your workout</span>
        <ChevronRight size={15} strokeWidth={1.8} />
      </div>
    </button>
  );
}

function QuickActionTile({ a, onClick }) {
  const Icon = a.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={a.testId}
      className="quick-action-tile"
    >
      <div className="quick-action-icon">
        <Icon size={20} strokeWidth={1.8} />
      </div>
      <div className="quick-action-text">
        <div className="quick-action-label">{a.label}</div>
        <div className="quick-action-sub">{a.sub}</div>
      </div>
    </button>
  );
}

function RecentProgressRow({ summary, onOpen }) {
  if (!summary) return null;
  const items = [
    { key: "outfits",  label: "Outfits saved", value: summary.saved_outfits || 0,    icon: ShoppingBag },
    { key: "scans",    label: "Meals scanned", value: summary.nutrition_scans || 0,  icon: Camera      },
    { key: "photos",   label: "Progress pics", value: summary.total_photos || 0,     icon: Sparkles    },
    { key: "messages", label: "Coach chats",   value: summary.coach_messages || 0,   icon: Heart       },
  ];
  return (
    <div data-testid="home-recent-progress">
      <div className="section-eyebrow">
        <span>RECENT PROGRESS</span>
        <button type="button" onClick={onOpen} className="section-link" data-testid="home-recent-link">
          See all <ChevronRight size={13} strokeWidth={2} />
        </button>
      </div>
      <div className="recent-progress-grid">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.key} className="recent-progress-chip" data-testid={`home-recent-${it.key}`}>
              <div className="recent-progress-chip-icon">
                <Icon size={13} strokeWidth={1.8} />
              </div>
              <div className="recent-progress-chip-num">{it.value}</div>
              <div className="recent-progress-chip-label">{it.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyCoachTipCard({ message, onOpenCoach }) {
  if (!message) return null;
  return (
    <button
      type="button"
      onClick={onOpenCoach}
      className="daily-tip-card"
      data-testid="home-daily-tip"
    >
      <div className="daily-tip-quote">&ldquo;</div>
      <div className="daily-tip-body">
        <div className="daily-tip-eyebrow">DAILY COACH TIP</div>
        <div className="daily-tip-message serif">{message}</div>
        <div className="daily-tip-cta">
          Continue with Coach <ChevronRight size={13} strokeWidth={2} />
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────

// EXPLORE IX NOVA — feature discovery grid rendered on Home. Every card
// routes to the SAME screen used by the Improve tab so behaviour, Premium
// gating, and analytics stay consistent across entry points.
const EXPLORE_FEATURES = [
  { key: "skincare",  Icon: Droplet,   title: "Skincare",         desc: "Analyze your skin, build your routine and discover personalized products.", route: "/improve/skincare",  premiumOnly: false },
  { key: "fitness",   Icon: Dumbbell,  title: "Fitness",          desc: "Personalized workouts and training plans built around your goals.",         route: "/improve/fitness",   premiumOnly: false },
  { key: "nutrition", Icon: Camera,    title: "Calorie Scanner",  desc: "Scan your food and estimate calories to stay on track.",                    route: "/nutrition/scan",    premiumOnly: true  },
  { key: "style",     Icon: Shirt,     title: "Style",            desc: "Rate outfits, identify clothing and discover similar pieces.",              route: "/style",             premiumOnly: false },
  { key: "grooming",  Icon: Scissors,  title: "Grooming",         desc: "Personalized hair, beard and grooming guidance.",                           route: "/improve/grooming",  premiumOnly: false },
  { key: "fragrance", Icon: Wind,      title: "Fragrance",        desc: "Discover fragrances based on your style, occasion, season and budget.",     route: "/improve/fragrance", premiumOnly: false },
  { key: "progress",  Icon: TrendingUp,title: "Progress",         desc: "Track your improvement, milestones and journey.",                           route: "/progress",          premiumOnly: false },
  { key: "coach",     Icon: Sparkles,  title: "AI Coach",         desc: "Get personalized guidance throughout your IX NOVA journey.",                route: "/coach",             premiumOnly: false },
];

function ExploreCard({ feat, isPremium, onOpen }) {
  const { Icon, title, desc, premiumOnly, key } = feat;
  const showBadge = premiumOnly; // free users see the badge; premium users see the feature unlocked
  return (
    <button
      type="button"
      className="explore-card"
      onClick={onOpen}
      data-testid={`home-explore-card-${key}`}
    >
      <div className="explore-card-icon">
        <Icon size={20} strokeWidth={1.7} />
      </div>
      <div className="explore-card-body">
        <div className="explore-card-title">
          {title}
          {showBadge && (
            <span className={`explore-card-badge ${isPremium ? "unlocked" : ""}`} data-testid={`home-explore-badge-${key}`}>
              <Lock size={9} strokeWidth={2} /> Premium
            </span>
          )}
        </div>
        <div className="explore-card-desc">{desc}</div>
      </div>
      <ChevronRight size={16} color="var(--text-faint)" />
    </button>
  );
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [quota, setQuota] = useState(null);
  const [streak, setStreak] = useState(null);
  const [summary, setSummary] = useState(null);
  const [todayCalories, setTodayCalories] = useState(null);
  const [todayScans, setTodayScans] = useState(0);
  const [brief, setBrief] = useState(null);
  const [profileComplete, setProfileComplete] = useState(true);
  const [celebrateMilestone, setCelebrateMilestone] = useState(null);

  useEffect(() => {
    api.get("/quota").then((r) => setQuota(r.data)).catch(() => setQuota(null));
    api.get("/streak").then((r) => setStreak(r.data)).catch(() => setStreak(null));
    api.get("/progress/summary").then((r) => {
      setSummary(r.data);
      // Detect newly unlocked milestones (atomic — never celebrates the same one twice)
      const newly = getNewlyUnlockedMilestones(r.data?.milestones || []);
      const enabled = getPref(PREF_CELEBRATIONS, true);
      if (enabled && newly.length > 0) {
        const justUnlocked = (r.data?.milestones || []).find((m) => m.id === newly[newly.length - 1]);
        if (justUnlocked) setCelebrateMilestone(justUnlocked);
      }
    }).catch(() => setSummary(null));
    api.get("/coach/daily-brief").then((r) => setBrief(r.data)).catch(() => setBrief(null));
    api.get("/nutrition/scans").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const scans = r.data?.scans || [];
      const todays = scans.filter((s) => (s.created_at || "").slice(0, 10) === today);
      setTodayScans(todays.length);
      const total = todays.reduce((sum, s) => sum + (s.scan?.total_calories || 0), 0);
      setTodayCalories(total);
    }).catch(() => { setTodayCalories(null); setTodayScans(0); });
    api.get("/profile").then((r) => {
      const complete = !!r.data?.complete;
      setProfileComplete(complete);
      // First-run redirect is now handled by <RequireOnboarding> in App.js,
      // which checks /profile/onboarding-status on every protected route and
      // sends the user to /onboarding until their 5 core fields are filled.
      // This used to live here behind a localStorage flag — that flag could
      // get set without the user actually finishing onboarding, leaving them
      // permanently bypassing the flow. App.js does it correctly.
    }).catch(() => setProfileComplete(true));
  }, [navigate]);

  const greeting = useMemo(() => greetingFor(new Date().getHours()), []);
  const firstName = user?.name ? user.name.split(" ")[0] : null;
  const milestones = summary?.milestones || [];

  const opportunityRoute = useMemo(() => {
    const dim = summary?.biggest_opportunity?.dimension;
    const map = {
      style: "/stylist",
      grooming: "/glow-up/analyze",
      skincare: "/improve/skincare",
      fitness: "/improve/fitness",
      nutrition: "/nutrition/scan",
      confidence: "/coach",
      consistency: "/stylist",
    };
    return map[dim] || "/improve";
  }, [summary]);

  return (
    <div className="screen with-nav fade-in" data-testid="home-screen">
      <div className="top-bar">
        <div className="logo-mark" data-testid="home-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <NovaLogo size={20} />
          <span>NOVA</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="icon-btn gold" onClick={() => navigate("/premium")} aria-label="Premium" data-testid="home-premium-btn">
            <Crown size={17} strokeWidth={1.7} />
          </button>
          <button className="icon-btn" onClick={logout} aria-label="Logout" data-testid="home-logout-btn">
            <LogOut size={16} strokeWidth={1.7} />
          </button>
        </div>
      </div>

      {/* Personal greeting */}
      <div className="home-hero" data-testid="home-greeting">
        <div className="home-hero-eyebrow">{greeting.toUpperCase()}</div>
        <h2 className="home-hero-title">
          {firstName ? <>Hey, <em className="serif">{firstName}</em>.</> : <em className="serif">Welcome back.</em>}
        </h2>
        <div className="home-hero-sub">
          {streak?.current
            ? <>You&apos;ve shown up <strong>{streak.current}</strong> day{streak.current === 1 ? "" : "s"} in a row. Let&apos;s keep going.</>
            : <>Small steps, every day. That&apos;s Nova.</>}
        </div>
      </div>

      {/* Smart in-app reminders — only renders when there's something to surface */}
      <RemindersBanner userId={user?.id} />

      {/* Glow Journey */}
      <GlowJourneyCard
        milestones={milestones.length ? milestones : Array(9).fill(0).map((_, i) => ({ id: `m${i}`, name: "—", sub: "", unlocked: false }))}
        onOpenProgress={() => navigate("/progress")}
      />

      {/* Streak + Today's activity */}
      <div className="home-row-2" style={{ marginTop: 14 }}>
        <StreakRing current={streak?.current || 0} active={streak?.active_today} longest={streak?.longest} />
        <TodayActivityCard
          caloriesToday={todayCalories}
          scansToday={todayScans}
          briefMessage={brief?.message}
        />
      </div>

      {/* Today's Focus — Fitness-first CTA (goes to workout section) */}
      <div style={{ marginTop: 14 }}>
        <TodayFocusCard
          opportunity={summary?.biggest_opportunity}
          onAct={() => navigate("/improve/fitness")}
        />
      </div>

      {/* Quick Actions */}
      <div style={{ marginTop: 18 }}>
        <div className="section-eyebrow"><span>QUICK ACTIONS</span></div>
        <div className="quick-action-grid" data-testid="home-quick-actions">
          {QUICK_ACTIONS.map((a) => (
            <QuickActionTile key={a.key} a={a} onClick={() => navigate(a.route)} />
          ))}
        </div>
      </div>

      {/* Recent Progress */}
      <div style={{ marginTop: 18 }}>
        <RecentProgressRow summary={summary} onOpen={() => navigate("/progress")} />
      </div>

      {/* Explore IX NOVA — feature discovery grid. Same routes as Improve. */}
      <div className="explore-section" data-testid="home-explore-section">
        <div className="explore-header">
          <div className="explore-eyebrow">EXPLORE IX NOVA</div>
          <div className="explore-heading">Everything you need to become your best self.</div>
        </div>
        <div className="explore-grid">
          {EXPLORE_FEATURES.map((feat) => (
            <ExploreCard
              key={feat.key}
              feat={feat}
              isPremium={!!user?.premium}
              onOpen={() => navigate(feat.route)}
            />
          ))}
        </div>
      </div>

      {/* Daily Coach Tip */}
      <div style={{ marginTop: 14 }}>
        <DailyCoachTipCard message={brief?.message} onOpenCoach={() => navigate("/coach")} />
      </div>

      {/* Build profile banner — only when truly incomplete */}
      {!profileComplete && (
        <button
          type="button"
          className="card"
          onClick={() => navigate("/profile-setup")}
          data-testid="home-profile-incomplete-banner"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", textAlign: "left", marginTop: 18, width: "100%" }}
        >
          <div>
            <div className="tiny" style={{ letterSpacing: "0.20em", textTransform: "uppercase", color: "var(--gold)" }}>
              BUILD YOUR PROFILE
            </div>
            <div style={{ marginTop: 4, fontSize: 14, color: "var(--text-dim)" }}>
              2 min — every recommendation gets sharper
            </div>
          </div>
          <ChevronRight size={18} color="var(--text-faint)" />
        </button>
      )}

      {/* Quota line */}
      {quota && !quota.premium && (
        <div className="tiny" style={{ marginTop: 18, color: "var(--text-faint)", textAlign: "center" }} data-testid="home-quota-line">
          {quota.remaining}/{quota.limit} actions left today · <button type="button" onClick={() => navigate("/premium")} className="gold-text" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>Upgrade</button>
        </div>
      )}

      {/* Bottom credit / signature */}
      <div className="home-footer-mark" data-testid="home-footer-mark">— Nova, one day at a time</div>

      {/* Milestone celebration — fires once per newly-unlocked milestone */}
      <MilestoneCelebration
        milestone={celebrateMilestone}
        enabled={getPref(PREF_CELEBRATIONS, true)}
        onDone={() => setCelebrateMilestone(null)}
      />
    </div>
  );
}
